import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";
import logger from "@cayo/shared/libs/logger";
import { DialogContext } from "../../services/Dialogs/dialog-context";
import { DialogId } from "../../services/Dialogs/dialog-ids";
import { DialogParamsById, DialogRegistry, DialogService } from "../../services/Dialogs/types";
import ErrorBoundary from "@cayo/shared/components/common/ErrorBoundary";
import ErrorModal from "../Dialogs/ErrorModal";
import { getDialogComponent } from "./get-dialog-component";

type DialogProviderProps = {
  children: ReactNode;
  dialogs: DialogRegistry;
};

const log = logger.getLogger("DialogService");

const DialogProvider: FC<DialogProviderProps> = ({ children, dialogs }) => {
  const [openState, setOpenState] = useState<Partial<Record<DialogId, boolean>>>({});
  const [paramsState, setParamsState] = useState<Partial<DialogParamsById>>({});

  const openById = useCallback(<K extends DialogId>(dialogId: K, params: DialogParamsById[K]) => {
    log.info({ event: "open", dialogId, params });

    setParamsState((prev) => ({ ...prev, [dialogId]: params }));
    setOpenState((prev) => ({ ...prev, [dialogId]: true }));
  }, []);

  const closeById = useCallback((dialogId: DialogId) => {
    log.info({ event: "close", dialogId });

    setParamsState((prev) => ({ ...prev, [dialogId]: undefined }));
    setOpenState((prev) => ({ ...prev, [dialogId]: false }));
  }, []);

  const service: DialogService = useMemo(() => ({ openById, closeById }), [closeById, openById]);

  const renderDialogById = <K extends DialogId>(dialogId: K) => {
    const DialogComponent = getDialogComponent(dialogs, dialogId);

    if (!DialogComponent) {
      return null;
    }

    const dialogParams = paramsState[dialogId];

    if (!openState[dialogId] || !dialogParams) {
      return null;
    }

    const onDialogClose = () => {
      dialogParams.onClose?.();
      closeById(dialogId);
    };

    const props = {
      ...dialogParams,
      onClose: onDialogClose,
    } as DialogParamsById[K];

    const handleErrorDismiss = (resetError: () => void) => {
      closeById(dialogId);
      resetError();
    };

    return (
      <ErrorBoundary
        key={dialogId}
        fallbackComponent={(resetError) => {
          log.error({ dialogId });

          return (
            <ErrorModal
              message={{
                text: "An error occurred while rendering the dialog",
                severity: "error",
              }}
              onDismiss={() => handleErrorDismiss(resetError)}
            />
          );
        }}
      >
        <DialogComponent {...props} />
      </ErrorBoundary>
    );
  };

  return (
    <DialogContext.Provider value={service}>
      {children}
      {(Object.keys(dialogs) as DialogId[]).map((dialogId) => renderDialogById(dialogId))}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
